import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaFilePdf,
  FaExclamationTriangle,
  FaDownload,
  FaExpand,
  FaCompress,
} from "react-icons/fa";

// Suppress all PDF.js console warnings and errors
if (typeof window !== "undefined") {
  // Override console methods for PDF.js only
  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;

  console.warn = function (...args) {
    if (
      args[0] &&
      typeof args[0] === "string" &&
      (args[0].includes("pdf") ||
        args[0].includes("worker") ||
        args[0].includes("PDF"))
    ) {
      return; // Silently ignore PDF-related warnings
    }
    originalConsoleWarn.apply(console, args);
  };

  console.error = function (...args) {
    if (
      args[0] &&
      typeof args[0] === "string" &&
      (args[0].includes("pdf") ||
        args[0].includes("worker") ||
        args[0].includes("PDF"))
    ) {
      return; // Silently ignore PDF-related errors
    }
    originalConsoleError.apply(console, args);
  };
}

// Import PDF.js dynamically to avoid blocking
let pdfjsLib = null;
let isPdfJsLoaded = false;

const SilentPDFViewer = ({ fileUrl, filename }) => {
  const [numPages, setNumPages] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const canvasRefs = useRef({});
  const containerRef = useRef(null);
  const isMountedRef = useRef(true);
  const retryTimerRef = useRef(null);
  const loadTimerRef = useRef(null);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getToolbarBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.9)" : "#f8fafc";

  // Load PDF.js library silently
  useEffect(() => {
    const loadPdfJs = async () => {
      if (!isPdfJsLoaded && !window.__pdfjs_loaded) {
        try {
          // Dynamically import PDF.js
          const pdfjs = await import("pdfjs-dist");
          pdfjsLib = pdfjs;

          // Set worker source silently - try multiple locations
          try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
          } catch (e) {
            try {
              pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
            } catch (e2) {
              // Silently fail - will use fallback
            }
          }

          isPdfJsLoaded = true;
          window.__pdfjs_loaded = true;
        } catch (err) {
          // Silent fail - will use iframe fallback
          console.debug("PDF.js not available");
        }
      }
    };

    loadPdfJs();
  }, []);

  // Check mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, []);

  // Load PDF silently with retry logic
  const loadPDF = useCallback(
    async (retry = false) => {
      if (!fileUrl || !pdfjsLib) return;

      if (retry) {
        setLoadAttempts((prev) => prev + 1);
      }

      try {
        const loadingTask = pdfjsLib.getDocument({
          url: fileUrl,
          disableAutoFetch: true,
          disableStream: true,
          disableRange: true,
          verbosity: 0, // Disable all logging
          useSystemFonts: false,
          disableFontFace: true,
        });

        const pdf = await loadingTask.promise;

        if (isMountedRef.current) {
          setPdfData(pdf);
          setNumPages(pdf.numPages);
          setLoading(false);
          setError(false);
          setRetryCount(0);
        }
      } catch (err) {
        // Silent error handling
        if (isMountedRef.current && retryCount < 2 && loadAttempts < 3) {
          setRetryCount((prev) => prev + 1);
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(
            () => {
              if (isMountedRef.current) {
                loadPDF(true);
              }
            },
            500 + retryCount * 300,
          );
        } else if (isMountedRef.current) {
          setError(true);
          setLoading(false);
        }
      }
    },
    [fileUrl, pdfjsLib, retryCount, loadAttempts],
  );

  // Load PDF when file URL changes
  useEffect(() => {
    if (fileUrl && pdfjsLib) {
      setLoading(true);
      setError(false);
      setPdfData(null);
      setNumPages(null);
      setLoadAttempts(0);
      setRetryCount(0);

      // Small delay to prevent race conditions
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
      loadTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          loadPDF();
        }
      }, 100);
    }
  }, [fileUrl, pdfjsLib, loadPDF]);

  // Render page on canvas
  const renderPage = useCallback(
    async (pageNumber) => {
      if (!pdfData || !canvasRefs.current[pageNumber]) return;

      const canvas = canvasRefs.current[pageNumber];
      if (!canvas) return;

      try {
        const page = await pdfData.getPage(pageNumber);
        const viewport = page.getViewport({ scale });
        const context = canvas.getContext("2d");

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          background: "white",
        };

        await page.render(renderContext).promise;
      } catch (err) {
        // Silent fail - just don't render this page
        console.debug("Page render failed");
      }
    },
    [pdfData, scale],
  );

  // Render visible pages
  useEffect(() => {
    if (!pdfData || !numPages) return;

    const renderVisiblePages = async () => {
      const container = containerRef.current;
      if (!container) return;

      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const startPage = Math.max(1, Math.floor(scrollTop / 400) + 1);
      const endPage = Math.min(
        numPages,
        Math.ceil((scrollTop + clientHeight) / 400) + 2,
      );

      for (let i = startPage; i <= endPage; i++) {
        if (canvasRefs.current[i]) {
          await renderPage(i);
        }
      }
    };

    renderVisiblePages();
  }, [pdfData, numPages, scale, renderPage]);

  // Handle scroll to render pages progressively
  const handleScroll = useCallback(() => {
    if (!pdfData || !numPages) return;

    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      const container = containerRef.current;
      if (!container) return;

      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const startPage = Math.max(1, Math.floor(scrollTop / 400) + 1);
      const endPage = Math.min(
        numPages,
        Math.ceil((scrollTop + clientHeight) / 400) + 2,
      );

      for (let i = startPage; i <= endPage; i++) {
        if (canvasRefs.current[i]) {
          renderPage(i);
        }
      }
    }, 100);
  }, [pdfData, numPages, renderPage]);

  const scrollTimerRef = useRef(null);

  // Add scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setScale(1);

  const getCanvasWidth = () => {
    if (isFullscreen) return Math.min(window.innerWidth - 100, 900);
    if (isMobile) return window.innerWidth - 60;
    return 500;
  };

  // Loading UI - Clean and modern
  const LoadingUI = () => (
    <div
      style={{
        padding: "3rem",
        textAlign: "center",
        background: getCardBackground(),
        backdropFilter: "blur(20px)",
        borderRadius: "20px",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${getBorderColor()}`,
      }}
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      >
        <FaSpinner size={40} color="#8b5cf6" />
      </motion.div>
      <p
        style={{
          color: getTextSecondary(),
          fontSize: "0.85rem",
          marginTop: "1rem",
        }}
      >
        Loading preview...
      </p>
    </div>
  );

  // Error UI - Minimal, with retry
  const ErrorUI = () => (
    <div
      style={{
        padding: "3rem",
        textAlign: "center",
        background: getCardBackground(),
        backdropFilter: "blur(20px)",
        borderRadius: "20px",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${getBorderColor()}`,
      }}
    >
      <div
        style={{
          width: "70px",
          height: "70px",
          background: "rgba(239,68,68,0.1)",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FaExclamationTriangle size={32} color="#ef4444" />
      </div>
      <p
        style={{
          marginTop: "1rem",
          color: getTextSecondary(),
          fontSize: "0.85rem",
        }}
      >
        Preview not available
      </p>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          setError(false);
          setLoading(true);
          loadPDF(true);
        }}
        style={{
          marginTop: "1rem",
          padding: "0.5rem 1.5rem",
          background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
          color: "white",
          border: "none",
          borderRadius: "30px",
          cursor: "pointer",
          fontSize: "0.8rem",
          fontWeight: "500",
          boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
        }}
      >
        Try Again
      </motion.button>
    </div>
  );

  // Iframe fallback for when PDF.js fails
  const IframeFallback = () => (
    <iframe
      src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        minHeight: "500px",
        borderRadius: "12px",
      }}
      title="PDF Viewer"
    />
  );

  if (!fileUrl) {
    return (
      <div
        style={{
          padding: "3rem",
          textAlign: "center",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <div
          style={{
            width: "70px",
            height: "70px",
            background: "rgba(139,92,246,0.1)",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <FaFilePdf size={32} color="#8b5cf6" />
        </div>
        <p style={{ marginTop: "1rem", color: getTextSecondary() }}>
          No document to preview
        </p>
      </div>
    );
  }

  // Use iframe fallback if PDF.js failed and retries exhausted
  if (error && loadAttempts >= 3) {
    return <IframeFallback />;
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: getCardBackground(),
        backdropFilter: "blur(20px)",
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${getBorderColor()}`,
      }}
    >
      {/* Toolbar - Modern and minimal */}
      {!loading && !error && numPages > 0 && (
        <div
          style={{
            padding: "0.6rem 1rem",
            background: getToolbarBackground(),
            borderBottom: `1px solid ${getBorderColor()}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "28px",
                height: "28px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaFilePdf size={14} color="white" />
            </div>
            <span style={{ fontSize: "0.75rem", color: getTextSecondary() }}>
              {filename || "Document"} • {numPages}{" "}
              {numPages === 1 ? "page" : "pages"}
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={zoomOut}
              style={{
                width: "30px",
                height: "30px",
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f1f5f9",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "1rem",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: getTextColor(),
              }}
            >
              −
            </motion.button>
            <span
              style={{
                fontSize: "0.75rem",
                minWidth: "45px",
                textAlign: "center",
                color: getTextColor(),
                fontWeight: "500",
              }}
            >
              {Math.round(scale * 100)}%
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={zoomIn}
              style={{
                width: "30px",
                height: "30px",
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f1f5f9",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "1rem",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: getTextColor(),
              }}
            >
              +
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetZoom}
              style={{
                padding: "0 0.8rem",
                height: "30px",
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f1f5f9",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "0.7rem",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: getTextColor(),
                fontWeight: "500",
              }}
            >
              Reset
            </motion.button>
          </div>
        </div>
      )}

      {/* Canvas Container */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: "auto",
          padding: "1rem",
          background: isDarkMode ? "#1a1a2e" : "#f0f0f0",
          minHeight: "450px",
        }}
      >
        {loading && <LoadingUI />}

        {error && <ErrorUI />}

        {!loading && !error && pdfData && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <motion.div
                key={`page-${i + 1}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                style={{
                  backgroundColor: "white",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <canvas
                  ref={(el) => (canvasRefs.current[i + 1] = el)}
                  style={{
                    width: `${getCanvasWidth() * scale}px`,
                    height: "auto",
                    display: "block",
                  }}
                  width={getCanvasWidth() * scale}
                  height={400 * scale}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SilentPDFViewer;
